"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";

/* Path dihitung dari app/layanan/rawat-inap/ - tempat berkas INI
   berada.
     ../../components, ../../i18n  -> naik dua tingkat ke app/
     ./rawat-inap                  -> tetangga di folder yang sama */
import Footer from "../../components/Footer";
import { useLanguage } from "../../i18n/LanguageProvider";
import {
  CATATAN_HARGA,
  DITINJAU,
  FOTO,
  KATEGORI_KAMAR,
  RAWAT_INAP,
} from "./rawat-inap";

/* ============================================================
   RAWAT INAP - /layanan/rawat-inap

   ============================================================
   YANG BERUBAH DI REVISI TAMPILAN INI

   1. DAFTAR HARGA JADI KARTU, bukan baris memanjang.

      Versi sebelumnya menaruh nama kamar di kiri dan harga di kanan,
      selebar 1140px. Di layar lebar, mata harus melompat sejauh
      sembilan ratus piksel dari nama ke angkanya - dan pada baris
      keempat orang mulai salah membaca harga milik baris sebelahnya.
      Masalah klasik tabel yang terlalu lebar.

      Kartu menyatukan nama, harga, dan kapasitas dalam satu blok
      sempit. Perbandingan antar kelas tetap mudah karena semuanya
      terlihat sekaligus.

   2. TIAP KARTU PUNYA TAUTAN "Tanya ketersediaan" SENDIRI, dengan
      pesan WhatsApp yang sudah menyebut nama kelasnya.

      Ini bagian yang paling berguna dan paling sering dilewatkan.
      Tanpa itu, pengunjung menekan satu tombol umum lalu harus
      mengetik ulang kelas yang tadi dilihatnya - dan admin harus
      bertanya balik. Dengan ini, pesan pertama sudah lengkap.

   3. FOTO PERTAMA NAIK KE KEPALA HALAMAN. Versi sebelumnya
      menyisakan separuh lebar layar kosong di samping judul,
      sementara kelima fotonya menumpuk di bawah.

   4. GALERI BISA DIPERBESAR. Klik foto membuka pratinjau layar penuh
      dengan tombol tutup, panah, dan tombol Esc.

   ============================================================
   className ditulis satu baris - lihat peringatan hydration di
   Facility.jsx. Jangan pakai string multi-baris di berkas ini.
   ============================================================ */

const LEBAR_HALAMAN = 1140;

/* Halaman induk /layanan sudah ada, jadi remah rotinya boleh jadi
   tautan. Pola dan alasannya sama dengan halaman layanan lain. */
const TAUTKAN_INDUK = true;

const TEKS = {
  eyebrow: { id: "Layanan Kami", en: "Our Services" },
  judul: { id: "Rawat Inap", en: "Inpatient Care" },
  sub: {
    id: "Tersedia beberapa pilihan kelas kamar sesuai kebutuhan dan kenyamanan Anda. Ketersediaan kamar berubah setiap hari - hubungi Admin Rawat Inap untuk memastikannya.",
    en: "Several room classes are available to suit your needs and comfort. Availability changes daily, so please contact our admissions team to check.",
  },

  hubungi: { id: "Hubungi Rawat Inap", en: "Contact Admissions" },

  nomorJudul: { id: "Informasi dan Reservasi", en: "Information and Booking" },
  nomorSub: {
    id: "Ketersediaan kamar dan rincian biaya paling cepat dijawab Admin Rawat Inap.",
    en: "Room availability and cost details are answered fastest by our admissions team.",
  },
  chatWa: { id: "Chat WhatsApp", en: "Chat on WhatsApp" },
  telepon: { id: "Telepon", en: "Call" },

  tanyaKamar: { id: "Tanya ketersediaan", en: "Check availability" },
  hargaTanya: { id: "Hubungi kami untuk harga terbaru", en: "Contact us for the current rate" },
  ditinjau: { id: "Harga terakhir ditinjau", en: "Rates last reviewed" },
  mulaiDari: { id: "Mulai dari", en: "From" },

  fotoJudul: { id: "Suasana Kamar", en: "Our Rooms" },
  fotoSub: {
    id: "Klik salah satu foto untuk melihatnya lebih besar.",
    en: "Select a photo to view it larger.",
  },
  perbesar: { id: "Perbesar foto", en: "Enlarge photo" },
  tutup: { id: "Tutup", en: "Close" },
  sebelumnya: { id: "Foto sebelumnya", en: "Previous photo" },
  berikutnya: { id: "Foto berikutnya", en: "Next photo" },

  lihatLayanan: { id: "Lihat layanan lainnya", en: "See other services" },
};

/* Pesan WhatsApp per kelas kamar. {kelas} diganti nama kelasnya.

   Ditulis sebagai satu kalimat utuh dengan sisipan, BUKAN dirakit
   dari potongan - urutan kata bahasa Indonesia dan Inggris berbeda,
   dan kalimat yang dirakit dari dua kunci akan kacau di salah
   satunya. */
const PESAN_KAMAR = {
  id: "Halo Admin Rawat Inap RS Jakarta Medical Center,\n\nSaya ingin menanyakan ketersediaan {kelas} ({kategori}).\n\nMohon informasinya. Terima kasih.",
  en: "Hello RS Jakarta Medical Center inpatient admissions,\n\nI would like to ask about the availability of {kelas} ({kategori}).\n\nThank you for your help.",
};

function pilihTeks(nilai, lang) {
  if (nilai == null) return "";
  if (typeof nilai === "string") return nilai;
  return nilai[lang] || nilai.id || "";
}

export default function RawatInapContent() {
  const { lang, t } = useLanguage();

  /* Berkas foto diisi manual di rawat-inap.js. Yang gagal dimuat
     dicatat di sini lalu disembunyikan, jadi kalau ternyata hanya ada
     tiga dari lima, dua sisanya tidak menyisakan kotak rusak. */
  const [fotoGagal, setFotoGagal] = useState(() => new Set());
  const tandaiGagal = (src) => setFotoGagal((s) => new Set(s).add(src));
  const fotoTampil = FOTO.filter((f) => f?.src && !fotoGagal.has(f.src));

  /* Indeks foto yang sedang dibuka besar. null berarti tertutup. */
  const [pratinjau, setPratinjau] = useState(null);

  const utama = RAWAT_INAP.telepon.find((n) => n.utama) ?? RAWAT_INAP.telepon[0];

  /* Esc menutup pratinjau, panah kiri-kanan berpindah foto.

     Pengguna keyboard mengharapkan Esc bekerja pada apa pun yang
     menutupi layar - tanpa itu, satu-satunya jalan keluar adalah
     mencari tombol tutup dengan Tab. */
  useEffect(() => {
    if (pratinjau === null) return;

    function tekan(e) {
      if (e.key === "Escape") setPratinjau(null);
      if (e.key === "ArrowRight") setPratinjau((i) => (i + 1) % fotoTampil.length);
      if (e.key === "ArrowLeft") setPratinjau((i) => (i - 1 + fotoTampil.length) % fotoTampil.length);
    }

    window.addEventListener("keydown", tekan);

    /* Halaman di belakang dikunci supaya tidak ikut tergulir saat
       pratinjau terbuka. Nilai aslinya disimpan dan dikembalikan,
       bukan disetel paksa ke "auto" - kalau ada bagian lain yang juga
       mengunci gulir, menyetelnya paksa akan membuka kunci itu. */
    const gulirAsli = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", tekan);
      document.body.style.overflow = gulirAsli;
    };
  }, [pratinjau, fotoTampil.length]);

  /* WhatsApp hanya MENGISI kolom ketik, tidak mengirim - keluarga
     pasien masih bisa menyunting pesannya sebelum menekan kirim. */
  function tautanWa(n) {
    const pesan = pilihTeks(n.pesanWa, lang);
    return `https://wa.me/${n.wa}?text=${encodeURIComponent(pesan)}`;
  }

  /* Tautan tanya-ketersediaan per kelas kamar. Jatuh ke nomor utama,
     dengan pesan yang sudah menyebut kelas DAN kategorinya.

     Kategorinya wajib ikut disebut: "Kelas Satu" ada di kamar dewasa
     maupun kamar anak, dengan harga berbeda. Tanpa kategori, admin
     menerima pesan yang ambigu dan harus bertanya balik - persis
     kerja yang seharusnya dihemat oleh tombol ini. */
  function tautanKamar(namaKelas, namaKategori) {
    if (!utama.wa) return `tel:${utama.tel}`;
    const pesan = pilihTeks(PESAN_KAMAR, lang)
      .replace("{kelas}", namaKelas)
      .replace("{kategori}", namaKategori);
    return `https://wa.me/${utama.wa}?text=${encodeURIComponent(pesan)}`;
  }

  const fotoKepala = fotoTampil[0] ?? null;

  return (
    <div className="w-full bg-[var(--c-surface)] pt-[64px] md:pt-[112px]">

      {/* ---------- BREADCRUMB ---------- */}
      <div className="border-b border-[var(--c-line)] bg-[var(--c-tint)]">
        <div className="mx-auto max-w-[1600px] px-4 py-3 sm:px-6 sm:py-3.5 lg:px-10">
          <nav aria-label="Breadcrumb" className="text-[13px] sm:text-sm">
            <ol className="flex flex-wrap items-center gap-y-1">
              <li className="flex items-center">
                <Link href="/" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline">
                  {t("header.nav.home")}
                </Link>
                <Pemisah />
              </li>
              <li className="flex items-center">
                {TAUTKAN_INDUK ? (
                  <Link href="/layanan" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline">
                    {t("header.menu.layanan.label")}
                  </Link>
                ) : (
                  <span className="text-[var(--c-muted)]">{t("header.menu.layanan.label")}</span>
                )}
                <Pemisah />
              </li>
              <li className="flex items-center">
                <span aria-current="page" className="font-bold text-[var(--c-brand)]">
                  {pilihTeks(TEKS.judul, lang)}
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* ---------- KEPALA: DUA KOLOM ----------
         Foto pertama naik ke sini. Versi sebelumnya menyisakan
         separuh lebar layar kosong di samping judul, sementara kelima
         fotonya menumpuk di bawah halaman. Di layar sempit keduanya
         menumpuk sendiri: teks dan tombol dulu, foto menyusul. */}
      <div className="bg-[var(--c-tint)]">
        <div className="mx-auto px-4 py-12 md:py-14" style={{ maxWidth: LEBAR_HALAMAN }}>
          <div className="grid items-center gap-10" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))" }}>

            <div className="min-w-0">
              <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)]">
                {pilihTeks(TEKS.eyebrow, lang)}
              </p>

              <h1 className="mt-2.5 text-balance font-bold leading-[1.08] tracking-tight text-[var(--c-ink)]" style={{ fontSize: "clamp(30px, 4.4vw, 44px)" }}>
                {pilihTeks(TEKS.judul, lang)}
              </h1>

              <p className="mt-4 max-w-[520px] text-[15.5px] leading-[1.7] text-[var(--c-body)]">
                {pilihTeks(TEKS.sub, lang)}
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                {utama.wa && (
                  <a
                    href={tautanWa(utama)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 rounded-[12px] bg-[var(--c-brand)] px-6 py-4 text-[16px] font-bold text-white shadow-sm transition-colors hover:bg-[var(--c-brand-strong)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]"
                  >
                    <IkonWhatsApp />
                    {pilihTeks(TEKS.hubungi, lang)}
                  </a>
                )}

                {/* Nomor yang SAMA lewat jalur telepon. Keluarga pasien
                   yang sedang di rumah sakit sering lebih cepat
                   menelepon daripada mengetik. */}
                <a
                  href={`tel:${utama.tel}`}
                  className="inline-flex items-center gap-2.5 rounded-[12px] border border-[var(--c-brand-300)] bg-[var(--c-surface)] px-6 py-4 text-[16px] font-bold text-[var(--c-brand)] transition-colors hover:bg-[var(--c-tint)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]"
                >
                  <IkonTelepon />
                  {utama.tampil}
                </a>
              </div>
            </div>

            {fotoKepala && (
              <div className="min-w-0">
                <button
                  type="button"
                  onClick={() => setPratinjau(0)}
                  aria-label={pilihTeks(TEKS.perbesar, lang)}
                  className="group relative block aspect-[4/3] w-full overflow-hidden rounded-2xl bg-[var(--c-surface)] ring-1 ring-[var(--c-brand-line)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]"
                >
                  <Image
                    src={fotoKepala.src}
                    fill
                    sizes="(min-width: 1024px) 540px, 92vw"
                    priority
                    alt={pilihTeks(fotoKepala.alt, lang)}
                    onError={() => tandaiGagal(fotoKepala.src)}
                    className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03] motion-reduce:transition-none"
                  />
                </button>
              </div>
            )}

          </div>
        </div>
      </div>

      <div className="mx-auto px-4 py-12 md:py-16" style={{ maxWidth: LEBAR_HALAMAN }}>

        {/* ---------- KELAS KAMAR, PER KATEGORI ----------
           Dirender dengan perulangan, bukan disalin per kategori.
           Menambah kategori ketiga - "Perawatan Khusus" yang ada di
           situs lama - cukup menambah satu objek di KATEGORI_KAMAR;
           berkas ini tidak perlu disentuh sama sekali. */}
        {KATEGORI_KAMAR.map((kategori, ki) => {
          const namaKategori = pilihTeks(kategori.judul, lang);

          return (
            <section key={kategori.key} className={ki > 0 ? "mt-12 md:mt-16" : ""}>
              <h2 className="text-xl font-bold text-[var(--c-ink)] md:text-2xl">
                {namaKategori}
              </h2>

              {/* Keterangan tingkat kategori. Muncul hanya kalau
                 kategorinya punya `catatan` di rawat-inap.js.

                 Dipakai oleh Perawatan Khusus, untuk menyatakan bahwa
                 penempatan di HCU, ICU, PICU, atau NICU ditentukan
                 dokter - bukan atas permintaan. Tanpa itu, halaman ini
                 terbaca seolah keluarga bisa "memesan" ICU seperti
                 memesan VIP Room.

                 Diletakkan SEBELUM daftar kamarnya, bukan sesudah:
                 keterangan yang mengubah cara membaca daftar harus
                 dibaca lebih dulu, bukan ditemukan setelahnya. */}
              {kategori.catatan && (
                <p className="mt-2.5 max-w-[680px] rounded-xl bg-[var(--c-tint)] px-4 py-3 text-[14px] leading-relaxed text-[var(--c-brand-strong)] ring-1 ring-[var(--c-brand-line)]">
                  {pilihTeks(kategori.catatan, lang)}
                </p>
              )}

              {/* Kartu, bukan baris memanjang. Di layar 1140px, baris
                 yang menaruh nama di kiri dan harga di kanan memaksa
                 mata melompat sejauh sembilan ratus piksel - dan pada
                 baris keempat orang mulai salah membaca harga milik
                 baris sebelahnya. */}
              <ul className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {kategori.kamar.map((k, i) => {
                  const nama = pilihTeks(k.nama, lang);

                  return (
                    <li key={`${kategori.key}-${i}`} className="flex h-full flex-col rounded-2xl bg-[var(--c-surface)] p-5 ring-1 ring-[var(--c-line)] transition-shadow hover:shadow-sm">
                      <p className="text-[16px] font-bold leading-snug text-[var(--c-ink)]">{nama}</p>

                      {k.kapasitas && (
                        <p className="mt-1.5 inline-flex w-fit rounded-full bg-[var(--c-line-soft)] px-2.5 py-1 text-[12px] font-semibold text-[var(--c-body)]">
                          {pilihTeks(k.kapasitas, lang)}
                        </p>
                      )}

                      {/* mt-auto mendorong harga dan tautannya ke dasar
                         kartu, sehingga semua kartu punya baris penutup
                         yang sejajar meski ada yang punya keterangan
                         kapasitas dan ada yang tidak. */}
                      <div className="mt-auto pt-4">
                        {k.periksa ? (
                          <p className="text-[14px] font-semibold text-[var(--c-muted)]">
                            {pilihTeks(TEKS.hargaTanya, lang)}
                          </p>
                        ) : (
                          <p className="text-[17px] font-bold leading-tight text-[var(--c-brand)]">
                            {pilihTeks(k.harga, lang)}
                          </p>
                        )}

                        {/* Pesannya sudah menyebut kelas dan kategori.
                           Tanpa itu, pengunjung harus mengetik ulang apa
                           yang tadi dilihatnya, dan admin harus bertanya
                           balik "dewasa atau anak?". */}
                        <a
                          href={tautanKamar(nama, namaKategori)}
                          target={utama.wa ? "_blank" : undefined}
                          rel={utama.wa ? "noopener noreferrer" : undefined}
                          className="mt-3 inline-flex items-center gap-1.5 text-[13.5px] font-bold text-[var(--c-brand)] underline-offset-4 transition hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]"
                        >
                          {utama.wa ? <IkonWhatsApp kecil /> : <IkonTelepon kecil />}
                          {pilihTeks(TEKS.tanyaKamar, lang)}
                        </a>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}

        {/* Keterangan yang berlaku untuk SELURUH kategori, ditulis
           sekali di bawah semuanya - bukan diulang lima belas kali
           seperti di halaman lama. Pengulangan sebanyak itu justru
           membuat orang berhenti membacanya, padahal ini keterangan
           terpenting sebelum masuk. */}
        <div className="mt-5 rounded-2xl bg-[var(--c-surface-2)] p-4 ring-1 ring-[var(--c-line)] md:p-5">
          <p className="text-[14px] leading-relaxed text-[var(--c-body)]">
            {pilihTeks(CATATAN_HARGA, lang)}
          </p>

          {/* Peringatan "harga dapat berubah" tanpa tanggal tidak
             membantu siapa pun - pengunjung tidak bisa menilai apakah
             angkanya masih masuk akal. */}
          {pilihTeks(DITINJAU, lang) && (
            <p className="mt-1.5 text-[13px] text-[var(--c-faint)]">
              {pilihTeks(TEKS.ditinjau, lang)}: {pilihTeks(DITINJAU, lang)}
            </p>
          )}
        </div>

        {/* ---------- GALERI ----------
           Foto pertama sudah tampil di kepala halaman, jadi di sini
           dimulai dari yang kedua - menampilkannya lagi hanya
           membuang satu petak. */}
        {fotoTampil.length > 1 && (
          <section className="mt-12 md:mt-16">
            <h2 className="text-xl font-bold text-[var(--c-ink)] md:text-2xl">
              {pilihTeks(TEKS.fotoJudul, lang)}
            </h2>
            <p className="mt-2 max-w-[640px] text-[15px] leading-relaxed text-[var(--c-body)]">
              {pilihTeks(TEKS.fotoSub, lang)}
            </p>

            <ul className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {fotoTampil.slice(1).map((f, i) => (
                <li key={f.src}>
                  {/* <button>, bukan <div onClick>. Tombol bisa
                     dijangkau Tab, ditekan dengan Enter atau spasi,
                     dan diumumkan pembaca layar sebagai sesuatu yang
                     bisa ditekan. */}
                  <button
                    type="button"
                    onClick={() => setPratinjau(i + 1)}
                    aria-label={pilihTeks(TEKS.perbesar, lang)}
                    className="group relative block aspect-[4/3] w-full overflow-hidden rounded-2xl bg-[var(--c-line-soft)] ring-1 ring-[var(--c-line)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]"
                  >
                    <Image
                      src={f.src}
                      fill
                      sizes="(min-width: 1024px) 270px, (min-width: 640px) 45vw, 92vw"
                      alt={pilihTeks(f.alt, lang)}
                      onError={() => tandaiGagal(f.src)}
                      className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04] motion-reduce:transition-none"
                    />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ---------- NOMOR ---------- */}
        <section className="mt-12 md:mt-16">
          <h2 className="text-xl font-bold text-[var(--c-ink)] md:text-2xl">
            {pilihTeks(TEKS.nomorJudul, lang)}
          </h2>
          <p className="mt-2 max-w-[640px] text-[15px] leading-relaxed text-[var(--c-body)]">
            {pilihTeks(TEKS.nomorSub, lang)}
          </p>

          <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {RAWAT_INAP.telepon.map((n) => (
              <li key={n.tel} className="rounded-2xl bg-[var(--c-surface)] p-5 ring-1 ring-[var(--c-line)]">
                <p className="text-[13px] font-semibold uppercase tracking-wider text-[var(--c-muted)]">
                  {pilihTeks(n.label, lang)}
                </p>
                <p className="mt-1.5 text-lg font-bold text-[var(--c-ink)]">{n.tampil}</p>

                <div className="mt-3.5 flex flex-wrap gap-2">
                  {n.wa && (
                    <a
                      href={tautanWa(n)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full bg-[var(--c-brand)] px-3.5 py-2 text-[13px] font-bold text-white transition-colors hover:bg-[var(--c-brand-strong)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]"
                    >
                      <IkonWhatsApp kecil />
                      {pilihTeks(TEKS.chatWa, lang)}
                    </a>
                  )}

                  <a
                    href={`tel:${n.tel}`}
                    className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[13px] font-bold text-[var(--c-brand)] ring-1 ring-[var(--c-line)] transition-colors hover:ring-[var(--c-brand)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]"
                  >
                    <IkonTelepon kecil />
                    {pilihTeks(TEKS.telepon, lang)}
                  </a>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <div className="mt-12 md:mt-16">
          <Link
            href="/layanan"
            className="inline-flex items-center gap-2 text-[15px] font-bold text-[var(--c-brand)] underline-offset-4 transition hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--c-brand-mid)]"
          >
            {pilihTeks(TEKS.lihatLayanan, lang)}
            <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>

      </div>

      {/* ---------- PRATINJAU FOTO ---------- */}
      {pratinjau !== null && fotoTampil[pratinjau] && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={pilihTeks(TEKS.fotoJudul, lang)}
          /* Klik pada latar gelap menutup pratinjau. Gambarnya sendiri
             menghentikan rambatan klik, supaya menekan gambar tidak
             ikut menutupnya. */
          onClick={() => setPratinjau(null)}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-sm"
        >
          <button
            type="button"
            onClick={() => setPratinjau(null)}
            aria-label={pilihTeks(TEKS.tutup, lang)}
            className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-6 w-6">
              <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>

          {fotoTampil.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setPratinjau((i) => (i - 1 + fotoTampil.length) % fotoTampil.length); }}
                aria-label={pilihTeks(TEKS.sebelumnya, lang)}
                className="absolute left-3 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:left-6"
              >
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-6 w-6">
                  <path d="m15 18-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setPratinjau((i) => (i + 1) % fotoTampil.length); }}
                aria-label={pilihTeks(TEKS.berikutnya, lang)}
                className="absolute right-3 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:right-6"
              >
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-6 w-6">
                  <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </>
          )}

          <div onClick={(e) => e.stopPropagation()} className="relative max-h-[85vh] w-full max-w-[1000px]">
            <Image
              src={fotoTampil[pratinjau].src}
              width={1600}
              height={1200}
              sizes="(min-width: 1024px) 1000px, 92vw"
              alt={pilihTeks(fotoTampil[pratinjau].alt, lang)}
              className="mx-auto h-auto max-h-[85vh] w-auto rounded-xl object-contain"
            />
          </div>

          <p className="absolute bottom-5 text-[13px] font-semibold text-white/70">
            {pratinjau + 1} / {fotoTampil.length}
          </p>
        </div>
      )}

      <Footer />
    </div>
  );
}

/* ============================================================
   IKON

   Digambar sendiri, bukan diimpor dari lucide-react. Ikon WhatsApp
   bukan bagian dari lucide - ia lambang merek, dan lucide sengaja
   tidak memuatnya.

   Keduanya aria-hidden: tulisan di sebelahnya sudah menyebutkan apa
   yang dilakukan tombolnya.
   ============================================================ */

function IkonWhatsApp({ kecil }) {
  const u = kecil ? 14 : 20;
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" width={u} height={u} className="shrink-0">
      <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.48-1.75-1.65-2.05-.17-.3-.02-.46.13-.61.14-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35z" />
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.28-1.38a9.9 9.9 0 0 0 4.76 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.85 9.85 0 0 0 12.04 2zm0 18.14h-.01a8.23 8.23 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.05-.2-.31a8.2 8.2 0 0 1-1.26-4.36c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.83 2.42a8.19 8.19 0 0 1 2.41 5.83c0 4.54-3.7 8.22-8.24 8.22z" />
    </svg>
  );
}

function IkonTelepon({ kecil }) {
  const u = kecil ? 14 : 20;
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" width={u} height={u} className="shrink-0">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Pemisah() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="mx-2 h-3.5 w-3.5 shrink-0 text-[var(--c-faint)] sm:mx-2.5">
      <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
